import React, { useEffect, useState, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTheme } from '../contexts/ThemeContext';
import { Palette, Eye, Download, Sparkles, Layers, Shield, Users, TrendingUp, Clock, CheckCircle, Pipette, ImageIcon, History, Zap, Heart, Coffee, Monitor, Star, Menu, X, Puzzle, Code, Briefcase, Home, Camera, Paintbrush } from 'lucide-react';
import Lenis from 'lenis';
import 'lenis/dist/lenis.css';
import SEO from './SEO';

const Homepage = () => {
  const { isDark, setIsDark } = useTheme();
  const location = useLocation();
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const lenisRef = useRef(null);

  // Mobile Detection State
  const [isMobile, setIsMobile] = useState(false);
  const [showMobileBanner, setShowMobileBanner] = useState(true);

  // Safari detection for optimized transitions
  const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

  // Initialize Lenis smooth scroll (Optimized for Safari)
  useEffect(() => {

    const lenis = new Lenis({
      duration: isSafari ? 0.8 : 1.0, // Faster on Safari
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)), // easeOutExpo
      direction: 'vertical',
      gestureDirection: 'vertical',
      smooth: !isSafari, // Disable smooth scroll on Safari
      mouseMultiplier: isSafari ? 0.5 : 1,
      smoothTouch: false,
      touchMultiplier: 2,
      infinite: false,
      lerp: isSafari ? 0.15 : 0.1, // Snappier on Safari
    });

    lenisRef.current = lenis;

    // Reset scroll position immediately when component mounts
    lenis.scrollTo(0, { immediate: true });
    window.scrollTo(0, 0);

    let rafId;
    function raf(time) {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    }

    rafId = requestAnimationFrame(raf);

    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
      lenisRef.current = null;
    };
  }, []);

  // Throttled mouse movement for better performance
  useEffect(() => {
    let rafId;
    let mouseX = 0;
    let mouseY = 0;

    const handleMouseMove = (e) => {
      mouseX = e.clientX;
      mouseY = e.clientY;

      if (!rafId) {
        rafId = requestAnimationFrame(() => {
          setMousePosition({ x: mouseX, y: mouseY });
          rafId = null;
        });
      }
    };

    window.addEventListener('mousemove', handleMouseMove, { passive: true });
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Handle hash navigation from other pages
  useEffect(() => {
    if (location.hash && lenisRef.current) {
      // Wait for page transition to complete, then scroll to hash
      const timeoutId = setTimeout(() => {
        const element = document.querySelector(location.hash);
        if (element) {
          lenisRef.current.scrollTo(element, {
            offset: -100, // Account for sticky header
            duration: 1.2,
            easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
          });
        }
      }, 500); // Wait for Framer Motion exit animation (400ms) + small buffer

      return () => clearTimeout(timeoutId);
    }
  }, [location.hash]);

  // Mobile Detection - Detects actual mobile/tablet devices
  useEffect(() => {
    const checkMobile = () => {
      // Check user agent for mobile devices
      const userAgent = navigator.userAgent || navigator.vendor || window.opera;
      const isMobileDevice = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent.toLowerCase());

      // Also check screen width as fallback
      const isSmallScreen = window.innerWidth < 768;

      setIsMobile(isMobileDevice || isSmallScreen);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const features = [
    {
      icon: <Palette className="w-6 h-6" />,
      title: "14+ Color Standards",
      description: "RAL, Pantone, HKS, NCS, Munsell, Federal Standard, and more. Bridge the gap between digital and physical color.",
      gradient: "from-blue-400 to-cyan-400",
      bgGradient: "from-blue-500/10 to-cyan-500/10",
      borderColor: "border-blue-cyan"
    },
    {
      icon: <Sparkles className="w-6 h-6" />,
      title: "Smart Color Harmonies",
      description: "Generate perfect complementary, triadic, and analogous palettes instantly. Science-backed color theory.",
      gradient: "from-purple-400 to-pink-400",
      bgGradient: "from-purple-500/10 to-pink-500/10",
      borderColor: "border-purple-pink"
    },
    {
      icon: <Eye className="w-6 h-6" />,
      title: "Accessibility Built-in",
      description: "WCAG compliance checker and color blindness simulation. Design for everyone, not just some.",
      gradient: "from-rose-400 to-orange-400",
      bgGradient: "from-rose-500/10 to-orange-500/10",
      borderColor: "border-rose-orange"
    },
    {
      icon: <Layers className="w-6 h-6" />,
      title: "Advanced Color Spaces",
      description: "LAB, LCH, OKLAB, OKLCH, HSB, CMYK. Professional-grade color precision for any medium.",
      gradient: "from-teal-400 to-emerald-400",
      bgGradient: "from-teal-500/10 to-emerald-500/10",
      borderColor: "border-teal-emerald"
    },
    {
      icon: <Download className="w-6 h-6" />,
      title: "Export Anywhere",
      description: "CSS, Tailwind, SCSS, JSON, SVG, PNG, Adobe ASE. One click to production.",
      gradient: "from-indigo-400 to-violet-400",
      bgGradient: "from-indigo-500/10 to-violet-500/10",
      borderColor: "border-indigo-violet"
    },
    {
      icon: <ImageIcon className="w-6 h-6" />,
      title: "Color Extraction",
      description: "Upload any image and extract its dominant colors instantly. Perfect for creating palettes from photos.",
      gradient: "from-amber-400 to-yellow-400",
      bgGradient: "from-amber-500/10 to-yellow-500/10",
      borderColor: "border-amber-yellow"
    },
    {
      icon: <Pipette className="w-6 h-6" />,
      title: "Eye Dropper Tool",
      description: "Pick colors directly from your screen. Sample from anywhere, anytime, with pixel-perfect accuracy.",
      gradient: "from-sky-400 to-blue-400",
      bgGradient: "from-sky-500/10 to-blue-500/10",
      borderColor: "border-sky-blue"
    },
    {
      icon: <Layers className="w-6 h-6" />,
      title: "Interactive Visualizer",
      description: "See your colors in action with real UI previews. Test your palette on actual design components.",
      gradient: "from-fuchsia-400 to-purple-400",
      bgGradient: "from-fuchsia-500/10 to-purple-500/10",
      borderColor: "border-fuchsia-purple"
    },
    {
      icon: <History className="w-6 h-6" />,
      title: "Color History",
      description: "Never lose a perfect color. Automatic history tracking keeps your recent selections at your fingertips.",
      gradient: "from-lime-400 to-green-400",
      bgGradient: "from-lime-500/10 to-green-500/10",
      borderColor: "border-lime-green"
    }
  ];

  const stats = [
    { value: "10,000+", label: "Palettes Generated", icon: <Palette className="w-5 h-5" /> },
    { value: "2,500+", label: "Active Users", icon: <Users className="w-5 h-5" /> },
    { value: "99.9%", label: "Uptime", icon: <TrendingUp className="w-5 h-5" /> },
    { value: "< 1s", label: "Export Speed", icon: <Clock className="w-5 h-5" /> }
  ];

  const testimonials = [
    {
      quote: "Very useful and pretty inspiring! The color palettes are harmonious, and the detailed information for each shade (RGB, HSL, and WCAG ratings) makes it perfect for both designers and developers",
      author: "Octavian Gensthaler",
      role: "Full-Stack Dev & Entrepreneur",
      avatar: "OG"
    },
    {
      quote: "Aaaa, this combines everything I need. Especially when choosing primary buttons for UI interfaces. I especially like the UI application visualization. Great tool, thank you for creating it!",
      author: "Silvia Bosoiu",
      role: "Creative Lead",
      avatar: "SB"
    },
    {
      quote: "Really awesome",
      author: "Diana Lized",
      role: "Fashion Designer & Story Teller",
      avatar: "DL"
    }
  ];

  const personas = [
    {
      icon: <Monitor className="w-6 h-6" />,
      role: "UI/UX Designers",
      benefit: "Design systems that scale",
      features: [
        "WCAG compliance checker for accessible designs",
        "Color blindness simulation with 9 variants",
        "Interactive UI visualizer with real components"
      ],
      gradient: "from-blue-400 to-cyan-400",
      bgGradient: "from-blue-500/10 to-cyan-500/10"
    },
    {
      icon: <Code className="w-6 h-6" />,
      role: "Developers",
      benefit: "From design to code, instantly",
      features: [
        "Export to CSS, Tailwind, SCSS, JSON in one click",
        "Command palette (Cmd+K) for keyboard workflow",
        "Copy-paste ready color variables"
      ],
      gradient: "from-purple-400 to-pink-400",
      bgGradient: "from-purple-500/10 to-pink-500/10"
    },
    {
      icon: <Briefcase className="w-6 h-6" />,
      role: "Product Managers",
      benefit: "Brand consistency across teams",
      features: [
        "Save and share brand color palettes",
        "Export palettes for team documentation",
        "Match industry standard colors (RAL, Pantone)"
      ],
      gradient: "from-rose-400 to-orange-400",
      bgGradient: "from-rose-500/10 to-orange-500/10"
    },
    {
      icon: <Home className="w-6 h-6" />,
      role: "Architects & Interior Designers",
      benefit: "Physical meets digital",
      features: [
        "14+ industry standards (RAL, NCS, Munsell)",
        "Match paint colors to digital designs",
        "Precise color specifications for materials"
      ],
      gradient: "from-teal-400 to-emerald-400",
      bgGradient: "from-teal-500/10 to-emerald-500/10"
    },
    {
      icon: <Camera className="w-6 h-6" />,
      role: "Content Creators & Marketers",
      benefit: "On-brand visuals, every time",
      features: [
        "Mood-based palette builder with 18 presets",
        "Extract colors from photos and images",
        "Social media-ready palette exports"
      ],
      gradient: "from-indigo-400 to-violet-400",
      bgGradient: "from-indigo-500/10 to-violet-500/10"
    },
    {
      icon: <Paintbrush className="w-6 h-6" />,
      role: "Artists & Illustrators",
      benefit: "Harmony in every stroke",
      features: [
        "Smart color harmonies (complementary, triadic)",
        "Match Copic and Prismacolor markers",
        "Generate shade variations instantly"
      ],
      gradient: "from-amber-400 to-yellow-400",
      bgGradient: "from-amber-500/10 to-yellow-500/10"
    }
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.4, ease: "easeInOut" }}
      className="min-h-screen bg-gray-50 dark:bg-gray-950 text-gray-900 dark:text-gray-100 transition-colors duration-500"
    >
      <SEO />
      {/* Skip to main content link for keyboard navigation */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 focus:px-4 focus:py-2 focus:bg-blue-600 focus:text-white focus:rounded-lg focus:shadow-lg"
      >
        Skip to main content
      </a>
      {/* Animated Background Orbs - Optimized */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden" aria-hidden="true">
        <div
          className="absolute w-[200px] h-[200px] md:w-[400px] md:h-[400px] lg:w-[500px] lg:h-[500px] rounded-full bg-gradient-to-r from-blue-400/10 to-cyan-400/10 dark:from-purple-400/20 dark:to-pink-400/20 blur-2xl md:blur-3xl transition-transform duration-1000 ease-out will-change-transform"
          style={{
            top: '10%',
            left: '5%',
            transform: `translate(${mousePosition.x * 0.02}px, ${mousePosition.y * 0.02}px)`
          }}
        />
        <div
          className="absolute w-[200px] h-[200px] md:w-[400px] md:h-[400px] lg:w-[500px] lg:h-[500px] rounded-full bg-gradient-to-r from-purple-400/10 to-pink-400/10 dark:from-blue-400/20 dark:to-cyan-400/20 blur-2xl md:blur-3xl transition-transform duration-1000 ease-out will-change-transform"
          style={{
            bottom: '10%',
            right: '5%',
            transform: `translate(${mousePosition.x * -0.02}px, ${mousePosition.y * -0.02}px)`
          }}
        />
      </div>

      {/* Header - Fade only, no movement */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.3, ease: "easeInOut" }}
        className="sticky top-4 z-50 px-4"
      >
        <div className="max-w-[1200px] mx-auto">
          <header className={`relative border border-gray-200/50 dark:border-gray-800/50 bg-white/90 dark:bg-gray-950/90 ${isMobileMenuOpen ? 'rounded-3xl' : 'rounded-full'} ${isScrolled ? 'shadow-lg shadow-gray-200/50 dark:shadow-gray-900/50' : ''}`} role="banner" style={{ backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)', transition: 'box-shadow 500ms' }}>
            <div className="px-6 py-3 flex justify-between items-center">
              <div className="flex items-center gap-3">
                <Link to="/" className="flex items-center gap-2 hover:opacity-80 transition-opacity" aria-label="Komorebi homepage">
                  <div className="relative">
                    <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-cyan-600 rounded-lg blur-lg opacity-50" />
                    <img src="/komorebi-logo.svg" alt="Komorebi - Professional Color Palette Generator Logo" className="w-6 h-6 relative z-10" />
                  </div>
                  <span className="text-base font-bold bg-gradient-to-r from-blue-600 to-cyan-600 bg-clip-text text-transparent">Komorebi</span>
                </Link>
                <nav className="hidden md:flex items-center gap-2" role="navigation" aria-label="Main navigation">
                  <a href="#features" className="px-4 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-all duration-300">Features</a>
                  <Link to="/story" className="px-4 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-all duration-300">Story</Link>
                  <Link to="/changelog" className="px-4 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-all duration-300">Changelog</Link>
                  <a
                    href="https://www.figma.com/community/plugin/1562754844917396329"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1.5 px-4 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-purple-600 dark:hover:text-purple-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-all duration-300"
                    aria-label="Get Komorebi Figma Plugin"
                  >
                    <Puzzle className="w-3.5 h-3.5" />
                    Figma Plugin
                  </a>
                  <a
                    href="https://www.producthunt.com/posts/komorebi-color-play?utm_source=other&utm_medium=social"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1.5 px-4 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-orange-600 dark:hover:text-orange-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-all duration-300"
                    aria-label="Review Komorebi on Product Hunt"
                  >
                    <Star className="w-3.5 h-3.5" />
                    Review
                  </a>
                </nav>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setIsDark(!isDark)}
                  className="px-3 py-1.5 rounded-full hover:bg-gray-200/80 dark:hover:bg-gray-800/80 transition-all duration-300 text-lg"
                  aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
                  aria-pressed={isDark}
                >
                  {isDark ? '☀️' : '🌙'}
                </button>
                <Link
                  to="/app"
                  className="hidden sm:inline-flex px-4 py-1.5 bg-gray-900 dark:bg-white text-white dark:text-gray-900 rounded-full font-semibold hover:scale-105 border border-gray-800 dark:border-gray-200 text-sm transition-all duration-300"
                  aria-label="Launch Komorebi color palette generator app"
                >
                  Launch App
                </Link>
                {/* Mobile Menu Button */}
                <button
                  onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                  className="md:hidden px-3 py-1.5 rounded-full hover:bg-gray-200/80 dark:hover:bg-gray-800/80 transition-all duration-300"
                  aria-label="Toggle mobile menu"
                  aria-expanded={isMobileMenuOpen}
                >
                  {isMobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {/* Mobile Menu Dropdown */}
            {isMobileMenuOpen && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2 }}
                className="md:hidden border-t border-gray-200 dark:border-gray-800 py-4 px-6 rounded-b-2xl"
              >
                <nav className="flex flex-col gap-2" role="navigation" aria-label="Mobile navigation">
                  <a
                    href="#features"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="px-4 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-all duration-300"
                  >
                    Features
                  </a>
                  <Link
                    to="/story"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="px-4 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-all duration-300"
                  >
                    Story
                  </Link>
                  <Link
                    to="/changelog"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="px-4 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-all duration-300"
                  >
                    Changelog
                  </Link>
                  <a
                    href="https://www.figma.com/community/plugin/1562754844917396329"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-purple-600 dark:hover:text-purple-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-all duration-300"
                  >
                    <Puzzle className="w-4 h-4" />
                    Figma Plugin
                  </a>
                  <a
                    href="https://www.producthunt.com/posts/komorebi-color-play?utm_source=other&utm_medium=social"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-orange-600 dark:hover:text-orange-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-all duration-300"
                  >
                    <Star className="w-4 h-4" />
                    Review on Product Hunt
                  </a>
                  <Link
                    to="/app"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="mt-2 px-4 py-2.5 bg-gray-900 dark:bg-white text-white dark:text-gray-900 rounded-lg font-semibold text-center border border-gray-800 dark:border-gray-200 text-sm transition-all duration-300"
                  >
                    Launch App
                  </Link>
                </nav>
              </motion.div>
            )}
          </header>
        </div>
      </motion.div>

      {/* Mobile Warning Banner */}
      {isMobile && showMobileBanner && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.3 }}
          className="relative z-40 mt-4 bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 border-b border-amber-200 dark:border-amber-800"
          role="alert"
          aria-live="polite"
        >
          <div className="max-w-7xl mx-auto px-4 py-3">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-3 flex-1">
                <Monitor className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0" />
                <p className="text-sm text-amber-900 dark:text-amber-100">
                  <span className="font-semibold">Best experienced on desktop.</span>
                  <span className="hidden sm:inline"> Some features may be limited on mobile devices.</span>
                </p>
              </div>
              <button
                onClick={() => setShowMobileBanner(false)}
                className="text-amber-600 dark:text-amber-400 hover:text-amber-800 dark:hover:text-amber-200 transition-colors px-2 py-1 text-sm font-medium"
                aria-label="Dismiss mobile warning"
              >
                Dismiss
              </button>
            </div>
          </div>
        </motion.div>
      )}

      {/* Hero Section - Slides up */}
      <motion.section
        id="main-content"
        initial={{ opacity: 0, ...(isSafari ? {} : { y: 30 }) }}
        animate={{ opacity: 1, ...(isSafari ? {} : { y: 0 }) }}
        exit={{ opacity: 0, ...(isSafari ? {} : { y: -30 }) }}
        transition={{ duration: isSafari ? 0.3 : 0.5, delay: isSafari ? 0 : 0.1, ease: "easeInOut" }}
        className="relative max-w-7xl mx-auto px-6 py-24 md:py-32 text-center"
        role="main"
        aria-labelledby="hero-heading"
      >
        <div className="space-y-8 relative z-10">
          {/* Animated Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-lg">
            <Sparkles className="w-4 h-4 text-blue-500 animate-pulse" />
            <span className="text-sm font-medium">Free • No Sign-up Required • Perfect for Makers</span>
          </div>

          {/* Main Heading - H1 for SEO (tiny label style) */}
          <h1 className="text-[10px] md:text-xs font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider opacity-70">
            Professional Color Palette Generator with Industry Standards
          </h1>

          {/* Secondary Headline */}
          <h2 id="hero-heading" className="text-6xl md:text-7xl lg:text-8xl font-extrabold leading-tight text-gray-900/90 dark:text-white/90 mt-3">
            <span className="inline-block animate-morph-gradient font-black">Colors</span> that
            <br />
            just <span className="inline-block font-black animate-bounce-subtle">work.</span>
          </h2>

          {/* Subheading with SEO keywords - concise version */}
          <p className="text-sm md:text-base text-gray-600 dark:text-gray-400 max-w-2xl mx-auto leading-snug">
            Professional color palette generator with <strong>14+ industry standards</strong> (RAL, Pantone, NCS), built-in <strong>WCAG accessibility checker</strong>, <strong>color blindness simulation</strong>, and instant exports to CSS, Tailwind, JSON, SVG & more.
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
            <Link
              to="/app"
              className="group relative px-8 py-4 bg-gray-900 dark:bg-white text-white dark:text-gray-900 rounded-xl font-bold text-lg transition-all duration-300 hover:scale-105 overflow-hidden border border-gray-800 dark:border-gray-200"
            >
              <span className="relative z-10">Launch App →</span>
            </Link>
            <a
              href="#features"
              className="px-8 py-4 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500 rounded-xl font-bold text-lg transition-all duration-300 text-gray-900 dark:text-white"
              aria-label="View features section"
            >
              See Features
            </a>
          </div>

          {/* Trust Indicators */}
          <div className="flex flex-wrap items-center justify-center gap-6 pt-8 text-sm text-gray-600 dark:text-gray-400">
            <div className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-500" />
              <span>No credit card required</span>
            </div>
            <div className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-blue-500" />
              <span>Privacy-first</span>
            </div>
            <div className="flex items-center gap-2">
              <Zap className="w-5 h-5 text-yellow-500" />
              <span>Instant access</span>
            </div>
          </div>
        </div>

        {/* App Preview - Video Demo */}
        <div className="mt-20 relative">
          <div className="absolute -inset-4 bg-gray-400/20 dark:bg-gray-400/30 rounded-3xl blur-2xl opacity-50" />
          <div className="relative rounded-2xl overflow-hidden border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-2xl">
            <div className="p-4 md:p-8">
              {/* Video for desktop/tablet, image fallback for mobile */}
              {isMobile ? (
                <div className="aspect-video rounded-xl overflow-hidden">
                  <img
                    src="/komorebi_ss.png"
                    alt="Komorebi Color Palette Generator - Professional color tools with industry standards"
                    className="w-full h-full object-cover"
                  />
                </div>
              ) : (
                <video
                  autoPlay
                  loop
                  muted
                  playsInline
                  className="w-full rounded-xl"
                  aria-label="Komorebi app demonstration video"
                >
                  <source src="/videos/komorebi-demo.mp4" type="video/mp4" />
                  <img
                    src="/komorebi_ss.png"
                    alt="Komorebi Color Palette Generator - Professional color tools with industry standards"
                    className="w-full h-full object-cover"
                  />
                </video>
              )}
            </div>
          </div>
        </div>
      </motion.section>

      {/* Scrolling Text Band */}
      <div className="relative py-4" aria-hidden="true">
        <div className="max-w-7xl mx-auto relative overflow-hidden bg-white/10 dark:bg-gray-950/10 border border-gray-200/50 dark:border-gray-800/50 rounded-full py-2 scrolling-pocket" style={{ backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)' }}>
          <div className="flex whitespace-nowrap animate-scroll-seamless">
            {/* Duplicate the content multiple times for seamless loop */}
            {[...Array(20)].map((_, i) => (
              <div key={i} className="flex items-center text-gray-900 dark:text-white font-light text-sm md:text-base">
                <span className="mx-8">Artists</span>
                <span className="mx-4">•</span>
                <span className="mx-8">Designers</span>
                <span className="mx-4">•</span>
                <span className="mx-8">Developers</span>
                <span className="mx-4">•</span>
                <span className="mx-8">Managers</span>
                <span className="mx-4">•</span>
                <span className="mx-8">Builders</span>
                <span className="mx-4">•</span>
                <span className="mx-8">Engineers</span>
                <span className="mx-4">•</span>
                <span className="mx-8">Architects</span>
                <span className="mx-4">•</span>
                <span className="mx-8">Makers</span>
                <span className="mx-4">•</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Stats Section - Staggered animation */}
      <motion.section
        initial={{ opacity: 0, ...(isSafari ? {} : { y: 30 }) }}
        animate={{ opacity: 1, ...(isSafari ? {} : { y: 0 }) }}
        exit={{ opacity: 0, ...(isSafari ? {} : { y: -30 }) }}
        transition={{ duration: isSafari ? 0.3 : 0.5, delay: isSafari ? 0 : 0.2, ease: "easeInOut" }}
        className="relative max-w-7xl mx-auto px-6 py-16"
        aria-label="Platform statistics"
      >
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {stats.map((stat, index) => (
            <div
              key={index}
              className="relative group"
            >
              <div className="absolute inset-0 bg-gray-400/10 dark:bg-gray-400/20 rounded-2xl blur-xl opacity-50 group-hover:opacity-100 transition-opacity duration-300" />
              <div className="animated-border-card border-gray relative p-6 rounded-2xl bg-white/90 dark:bg-gray-800/90 border border-gray-300 dark:border-gray-700 text-center hover:border-gray-400 dark:hover:border-gray-600 transition-all duration-300">
                <div className="flex justify-center mb-2 text-gray-700 dark:text-gray-300">
                  {stat.icon}
                </div>
                <div className="text-3xl font-bold text-gray-900 dark:text-white">
                  {stat.value}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  {stat.label}
                </div>
              </div>
            </div>
          ))}
        </div>
      </motion.section>

      {/* Features Grid - Staggered animation */}
      <motion.section
        initial={{ opacity: 0, ...(isSafari ? {} : { y: 30 }) }}
        animate={{ opacity: 1, ...(isSafari ? {} : { y: 0 }) }}
        exit={{ opacity: 0, ...(isSafari ? {} : { y: -30 }) }}
        transition={{ duration: isSafari ? 0.3 : 0.5, delay: isSafari ? 0 : 0.3, ease: "easeInOut" }}
        id="features"
        className="relative max-w-7xl mx-auto px-6 py-20"
        aria-labelledby="features-heading"
      >
        <div className="text-center mb-16">
          <h2 id="features-heading" className="text-4xl md:text-5xl font-bold mb-4 text-gray-900 dark:text-white">
            Comprehensive Color Tools for <span className="bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">Makers</span>
          </h2>
          <p className="text-xl text-gray-600 dark:text-gray-400">
            Professional-grade color palette features with industry-standard color matching and accessibility testing
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {features.map((feature, index) => (
            <div
              key={index}
              className="group relative"
            >
              <div className={`absolute inset-0 bg-gradient-to-r ${feature.bgGradient} rounded-2xl blur-2xl opacity-50 group-hover:opacity-100 transition-opacity duration-500`} />
              <div className={`animated-border-card ${feature.borderColor} relative p-8 rounded-2xl bg-white/90 dark:bg-gray-800/90 border border-gray-300 dark:border-gray-700 hover:border-gray-400 dark:hover:border-gray-600 transition-all duration-300 hover:-translate-y-2 h-full`}>
                <div className={`w-14 h-14 rounded-xl bg-gradient-to-r ${feature.gradient} flex items-center justify-center text-white mb-4 group-hover:scale-110 transition-transform duration-300 border border-white/20`}>
                  {feature.icon}
                </div>
                <h3 className="text-xl font-bold mb-3">{feature.title}</h3>
                <p className="text-gray-600 dark:text-gray-400 leading-relaxed">{feature.description}</p>
              </div>
            </div>
          ))}
        </div>
      </motion.section>

      {/* Value Proposition for Personas - Staggered animation */}
      <motion.section
        initial={{ opacity: 0, ...(isSafari ? {} : { y: 30 }) }}
        animate={{ opacity: 1, ...(isSafari ? {} : { y: 0 }) }}
        exit={{ opacity: 0, ...(isSafari ? {} : { y: -30 }) }}
        transition={{ duration: isSafari ? 0.3 : 0.5, delay: isSafari ? 0 : 0.35, ease: "easeInOut" }}
        className="relative max-w-7xl mx-auto px-6 py-20"
        aria-labelledby="personas-heading"
        aria-describedby="personas-description"
      >
        <div className="text-center mb-16">
          <h2 id="personas-heading" className="text-4xl md:text-5xl font-bold mb-4 text-gray-900 dark:text-white">
            Built for Every <span className="bg-gradient-to-r from-blue-600 to-cyan-600 bg-clip-text text-transparent">Creative Professional</span>
          </h2>
          <p id="personas-description" className="text-xl text-gray-600 dark:text-gray-400">
            From design to development, Komorebi adapts to your workflow, whether you're a designer, developer, product manager, architect, content creator, or artist
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" role="list">
          {personas.map((persona, index) => (
            <article
              key={index}
              className="group relative"
              role="listitem"
              aria-label={`${persona.role}: ${persona.benefit}`}
            >
              <div className={`absolute inset-0 bg-gradient-to-r ${persona.bgGradient} rounded-2xl blur-2xl opacity-50 group-hover:opacity-100 transition-opacity duration-500`} aria-hidden="true" />
              <div className="relative p-8 rounded-2xl bg-white/90 dark:bg-gray-800/90 border border-gray-300 dark:border-gray-700 hover:border-gray-400 dark:hover:border-gray-600 transition-all duration-300 hover:-translate-y-2 h-full">
                <div className={`w-14 h-14 rounded-xl bg-gradient-to-r ${persona.gradient} flex items-center justify-center text-white mb-4 group-hover:scale-110 transition-transform duration-300 border border-white/20`} aria-hidden="true">
                  {persona.icon}
                </div>
                <h3 className="text-xl font-bold mb-2 text-gray-900 dark:text-white">{persona.role}</h3>
                <p className="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-4">
                  {persona.benefit}
                </p>
                <ul className="space-y-2" aria-label={`Benefits for ${persona.role}`}>
                  {persona.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-start gap-2 text-sm text-gray-600 dark:text-gray-400">
                      <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" aria-hidden="true" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </article>
          ))}
        </div>
      </motion.section>

      {/* Social Proof / Testimonials - Staggered animation */}
      <motion.section
        initial={{ opacity: 0, ...(isSafari ? {} : { y: 30 }) }}
        animate={{ opacity: 1, ...(isSafari ? {} : { y: 0 }) }}
        exit={{ opacity: 0, ...(isSafari ? {} : { y: -30 }) }}
        transition={{ duration: isSafari ? 0.3 : 0.5, delay: isSafari ? 0 : 0.45, ease: "easeInOut" }}
        className="relative max-w-7xl mx-auto px-6 py-20"
        aria-labelledby="testimonials-heading"
      >
        <div className="text-center mb-16">
          <h2 id="testimonials-heading" className="text-4xl md:text-5xl font-bold mb-4 text-gray-900 dark:text-white">
            Trusted by Makers <span className="text-gray-900/80 dark:text-white/80">Worldwide</span>
          </h2>
          <p className="text-xl text-gray-600 dark:text-gray-400">
            Join thousands of creative professionals using Komorebi for their color workflow
          </p>
        </div>
        <div className="grid md:grid-cols-3 gap-8">
          {testimonials.map((testimonial, index) => (
            <div
              key={index}
              className="relative group"
            >
              <div className="absolute inset-0 bg-gray-400/10 dark:bg-gray-400/20 rounded-2xl blur-xl opacity-50 group-hover:opacity-100 transition-opacity duration-300" />
              <div className="animated-border-card border-gray relative p-8 rounded-2xl bg-white/90 dark:bg-gray-800/90 border border-gray-300 dark:border-gray-700 hover:border-gray-400 dark:hover:border-gray-600 transition-all duration-300">
                <p className="text-gray-700 dark:text-gray-300 mb-6 italic leading-relaxed">
                  "{testimonial.quote}"
                </p>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-gray-700 dark:bg-gray-300 flex items-center justify-center text-white dark:text-gray-900 font-bold border border-gray-800 dark:border-gray-400">
                    {testimonial.avatar}
                  </div>
                  <div>
                    <div className="font-semibold">{testimonial.author}</div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">{testimonial.role}</div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </motion.section>

      {/* Final CTA Section - Fade and scale */}
      <motion.section
        initial={{ opacity: 0, ...(isSafari ? {} : { scale: 0.95 }) }}
        animate={{ opacity: 1, ...(isSafari ? {} : { scale: 1 }) }}
        exit={{ opacity: 0, ...(isSafari ? {} : { scale: 0.95 }) }}
        transition={{ duration: isSafari ? 0.3 : 0.5, delay: isSafari ? 0 : 0.55, ease: "easeInOut" }}
        className="relative max-w-5xl mx-auto px-6 py-20 mb-20"
      >
        <div className="relative">
          <div className="absolute inset-0 bg-gray-400/20 dark:bg-gray-400/30 rounded-3xl blur-2xl opacity-50" />
          <div className="relative p-12 md:p-16 rounded-3xl bg-gray-900 dark:bg-white text-white dark:text-gray-900 text-center overflow-hidden border border-gray-800 dark:border-gray-200">
            <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0id2hpdGUiIHN0cm9rZS1vcGFjaXR5PSIwLjEiIHN0cm9rZS13aWR0aD0iMSIvPjwvcGF0dGVybj48L2RlZnM+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0idXJsKCNncmlkKSIvPjwvc3ZnPg==')] opacity-20" />
            <div className="relative z-10">
              <h2 className="text-4xl md:text-5xl font-bold mb-4">
                Start Creating Professional Color Palettes Today
              </h2>
              <p className="text-xl mb-8 opacity-90">
                Free color palette generator with industry standards. Create, test, and export in seconds. No signup, no credit card required.
              </p>
              <Link
                to="/app"
                className="inline-flex items-center gap-2 px-10 py-4 bg-white dark:bg-gray-900 text-gray-900 dark:text-white rounded-xl font-bold text-lg hover:scale-105 border border-gray-200 dark:border-gray-800 transition-all duration-300"
              >
                <Sparkles className="w-5 h-5" />
                Launch App Free
              </Link>
              <p className="text-sm mt-6 opacity-75">
                Share your creations with <span className="font-semibold">#komorebicolorplay</span>
              </p>
            </div>
          </div>
        </div>
      </motion.section>

      {/* Footer - Slide up from bottom */}
      <motion.footer
        initial={{ opacity: 0, ...(isSafari ? {} : { y: 20 }) }}
        animate={{ opacity: 1, ...(isSafari ? {} : { y: 0 }) }}
        exit={{ opacity: 0, ...(isSafari ? {} : { y: 20 }) }}
        transition={{ duration: isSafari ? 0.3 : 0.5, delay: isSafari ? 0 : 0.65, ease: "easeInOut" }}
        className="relative border-t border-gray-200/50 dark:border-gray-800/50 bg-white/90 dark:bg-gray-950/90 py-12"
        role="contentinfo"
      >
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
              <img src="/komorebi-logo.svg" alt="Komorebi" className="w-6 h-6" />
              <span className="font-semibold">Komorebi</span>
            </Link>
            <div className="flex gap-6 text-sm text-gray-600 dark:text-gray-400">
              <a href="#features" className="hover:text-blue-600 transition-colors duration-300">Features</a>
              <Link to="/story" className="hover:text-blue-600 transition-colors duration-300">Story</Link>
              <Link to="/changelog" className="hover:text-blue-600 transition-colors duration-300">Changelog</Link>
              <Link to="/app" className="hover:text-blue-600 transition-colors duration-300">App</Link>
              <a href="mailto:hello@obelisk.agency" className="hover:text-blue-600 transition-colors duration-300">Contact</a>
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
              <span>made with</span>
              <Heart className="h-4 w-4 text-red-500 fill-current" />
              <span>and</span>
              <Coffee className="h-4 w-4 text-amber-600" />
              <span>by</span>
              <a
                href="https://obelisk.agency"
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 transition-colors duration-200"
              >
                obelisk
              </a>
            </div>
          </div>
        </div>
      </motion.footer>
    </motion.div>
  );
};

export default Homepage;
